import db from "./localDB";
import supabase from "../Config/Supabase";

// Utility function to validate UUID
const isValidUUID = (uuid) => {
  if (!uuid || typeof uuid !== "string") return false;
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
};

// Utility function to ensure valid date
const ensureValidDate = (date) => {
  if (!date) return new Date().toISOString();
  if (date instanceof Date) return date.toISOString();
  if (typeof date === "string") return date;
  return new Date().toISOString();
};

// Save farmer data offline
export const saveFarmerOffline = async (farmerData, bundleData) => {
  try {
    // Validate required fields
    if (!farmerData.uuid || !isValidUUID(farmerData.uuid)) {
      throw new Error("Invalid or missing farmer UUID");
    }

    // Prepare farmer data with validation
    const validatedFarmerData = {
      ...farmerData,
      created_at: ensureValidDate(farmerData.created_at),
      synced: false,
    };

    // Save farmer data
    await db.offlineFarmers.add(validatedFarmerData);

    // Save bundle data if provided
    if (bundleData && Array.isArray(bundleData) && bundleData.length > 0) {
      const validatedBundles = bundleData.map((bundle) => {
        if (!bundle.farmer_id || !isValidUUID(bundle.farmer_id)) {
          throw new Error(`Invalid farmer_id in bundle: ${bundle.farmer_id}`);
        }

        return {
          ...bundle,
          farmer_id: bundle.farmer_id,
          created_at: ensureValidDate(bundle.created_at),
          synced: false,
        };
      });

      await db.offlineBundles.bulkAdd(validatedBundles);
    }

    return true;
  } catch (error) {
    console.error("Error saving offline data:", error);
    throw error;
  }
};

// Get all unsynced farmers
export const getUnsyncedFarmers = async () => {
  try {
    return await db.offlineFarmers
      .filter((farmer) => farmer.synced === false)
      .toArray();
  } catch (fallbackError) {
    console.error("Fallback query also failed:", fallbackError);
    return [];
  }
};

// Get all unsynced bundles for a farmer
export const getUnsyncedBundles = async (farmerUuid) => {
  try {
    // Validate farmer UUID
    if (!farmerUuid || !isValidUUID(farmerUuid)) {
      console.warn("Invalid farmer UUID provided:", farmerUuid);
      return [];
    }

    return await db.offlineBundles
      .where("farmer_id")
      .equals(farmerUuid)
      .and((bundle) => !bundle.synced)
      .toArray();
  } catch (error) {
    console.error("Error getting unsynced bundles:", error);
    // Fallback to filter method
    try {
      return await db.offlineBundles
        .filter((bundle) => bundle.farmer_id === farmerUuid && !bundle.synced)
        .toArray();
    } catch (fallbackError) {
      console.error("Fallback bundle query also failed:", fallbackError);
      return [];
    }
  }
};

// function that normalize data
const normalizeBigInt = (value) => {
  if (value === "" || value === null || value === undefined) {
    return 0;
  }

  const parsed = Number(value);

  return Number.isNaN(parsed) ? 0 : parsed;
};

// Improved sync single farmer to Supabase with better transaction handling
export const syncFarmerToSupabase = async (farmer) => {
  try {
    // Get associated bundles before starting transaction
    const bundles = await getUnsyncedBundles(farmer.uuid);

    // Upload farmer data to Supabase
    const farmerForSync = {
      ...farmer,

      number_of_people_in_house_hold: normalizeBigInt(
        farmer.number_of_people_in_house_hold,
      ),

      number_of_children_under_5: normalizeBigInt(
        farmer.number_of_children_under_5,
      ),

      number_of_breast_feeding_children: normalizeBigInt(
        farmer.number_of_breast_feeding_children,
      ),
    };
    const { data: farmerResponse, error: farmerError } = await supabase
      .from("farmer")
      .insert([farmerForSync])
      .select();

    if (farmerError) throw farmerError;

    // Upload bundles if they exist
    if (bundles.length > 0) {
      const bundlesForSync = bundles.map(
        ({ synced, id: localBundleId, ...bundle }) => ({
          ...bundle,
          farmer_id: farmer.uuid,
        }),
      );

      const { error: bundleError } = await supabase
        .from("farmer_bundle")
        .insert(bundlesForSync);

      if (bundleError) throw bundleError;
    }

    // After successful upload, remove from local storage
    try {
      // Remove bundles first
      if (bundles.length > 0) {
        const validBundleIds = bundles
          .map((b) => b.id)
          .filter((id) => id != null && id !== undefined);

        if (validBundleIds.length > 0) {
          await db.offlineBundles.bulkDelete(validBundleIds);
        }
      }

      // Remove farmer
      if (farmer?.id != null && farmer?.id !== undefined) {
        await db.offlineFarmers.delete(farmer.id);
      }
    } catch (deleteError) {
      console.error("Error removing local data after sync:", deleteError);
      // Don't throw here as the sync was successful
    }

    return farmerResponse[0];
  } catch (error) {
    console.error("Error syncing farmer:", error);
    throw error;
  }
};

// Sync all unsynced data
export const syncAllOfflineData = async () => {
  try {
    const unsyncedFarmers = await getUnsyncedFarmers();

    if (unsyncedFarmers.length === 0) {
      return { success: true, synced: 0 };
    }

    let successCount = 0;
    let errorCount = 0;
    const errors = [];

    for (const farmer of unsyncedFarmers) {
      try {
        await syncFarmerToSupabase(farmer);
        successCount++;
      } catch (error) {
        console.error(
          `Failed to sync farmer ${farmer.name || farmer.uuid}:`,
          error,
        );
        errorCount++;
        errors.push({
          farmer: farmer.name || farmer.uuid,
          error: error.message,
        });
      }
    }

    return {
      success: errorCount === 0,
      synced: successCount,
      errors: errorCount,
      errorDetails: errors,
    };
  } catch (error) {
    console.error("Error during bulk sync:", error);
    throw error;
  }
};

// Get all offline farmers (for display)
export const getAllOfflineFarmers = async () => {
  try {
    return await db.offlineFarmers.toArray();
  } catch (fallbackError) {
    console.error("Fallback query failed:", fallbackError);
    return [];
  }
};

// Improved cleanup function - delete data regardless of sync status
export const cleanupSyncedData = async (forceDelete = false) => {
  try {
    let farmersToDelete, bundlesToDelete;

    if (forceDelete) {
      // Delete all data regardless of sync status
      farmersToDelete = await db.offlineFarmers.toArray();
      bundlesToDelete = await db.offlineBundles.toArray();
    } else {
      // Delete only synced data
      farmersToDelete = await db.offlineFarmers
        .filter((farmer) => farmer.synced === true)
        .toArray();
      bundlesToDelete = await db.offlineBundles
        .filter((bundle) => bundle.synced === true)
        .toArray();
    }

    // Delete farmers
    if (farmersToDelete.length > 0) {
      const validFarmerIds = farmersToDelete
        .map((f) => f.id)
        .filter((id) => id != null && id !== undefined);

      if (validFarmerIds.length > 0) {
        await db.offlineFarmers.bulkDelete(validFarmerIds);
      }
    }

    // Delete bundles
    if (bundlesToDelete.length > 0) {
      const validBundleIds = bundlesToDelete
        .map((b) => b.id)
        .filter((id) => id != null && id !== undefined);

      if (validBundleIds.length > 0) {
        await db.offlineBundles.bulkDelete(validBundleIds);
      }
    }
  } catch (error) {
    console.error("Error cleaning up data:", error);
    // Try alternative cleanup method - delete one by one
    try {
      const condition = forceDelete
        ? () => true
        : (item) => item.synced === true;

      const farmersToDelete = await db.offlineFarmers
        .filter(condition)
        .toArray();
      const bundlesToDelete = await db.offlineBundles
        .filter(condition)
        .toArray();

      for (const farmer of farmersToDelete) {
        if (farmer.id != null && farmer.id !== undefined) {
          try {
            await db.offlineFarmers.delete(farmer.id);
          } catch (deleteError) {
            console.warn(`Failed to delete farmer ${farmer.id}:`, deleteError);
          }
        }
      }

      for (const bundle of bundlesToDelete) {
        if (bundle.id != null && bundle.id !== undefined) {
          try {
            await db.offlineBundles.delete(bundle.id);
          } catch (deleteError) {
            console.warn(`Failed to delete bundle ${bundle.id}:`, deleteError);
          }
        }
      }
    } catch (fallbackError) {
      console.error("Fallback cleanup also failed:", fallbackError);
    }
  }
};

// New function to delete all local data regardless of sync status
export const deleteAllLocalData = async () => {
  try {
    await cleanupSyncedData(true);
    return { success: true, message: "All local data deleted successfully" };
  } catch (error) {
    console.error("Error deleting all local data:", error);
    throw error;
  }
};

// Utility function to check database health
export const checkDatabaseHealth = async () => {
  try {
    const farmerCount = await db.offlineFarmers.count();
    const bundleCount = await db.offlineBundles.count();
    const unsyncedFarmerCount = await db.offlineFarmers
      .where("synced")
      .equals(false)
      .count();
    const unsyncedBundleCount = await db.offlineBundles
      .where("synced")
      .equals(false)
      .count();

    return {
      farmers: farmerCount,
      bundles: bundleCount,
      unsyncedFarmers: unsyncedFarmerCount,
      unsyncedBundles: unsyncedBundleCount,
    };
  } catch (error) {
    console.error("Error checking database health:", error);
    return null;
  }
};

// Delete a specific offline farmer and their associated bundles
export const deleteOfflineFarmer = async (farmer) => {
  try {
    if (!farmer) {
      throw new Error("Farmer data is required");
    }

    if (!farmer.uuid || !isValidUUID(farmer.uuid)) {
      throw new Error("Invalid farmer UUID");
    }

    // Delete farmer and associated bundles together
    await db.transaction(
      "rw",
      db.offlineFarmers,
      db.offlineBundles,
      async () => {
        // Get farmer's bundles
        let bundles = [];

        try {
          bundles = await db.offlineBundles
            .where("farmer_id")
            .equals(farmer.uuid)
            .toArray();
        } catch (error) {
          // Fallback if farmer_id is not indexed
          bundles = await db.offlineBundles
            .filter((bundle) => bundle.farmer_id === farmer.uuid)
            .toArray();
        }

        // Delete associated bundles
        if (bundles.length > 0) {
          const bundleIds = bundles
            .map((bundle) => bundle.id)
            .filter((id) => id !== null && id !== undefined);

          if (bundleIds.length > 0) {
            await db.offlineBundles.bulkDelete(bundleIds);
          }
        }

        // Delete farmer
        if (farmer.id !== null && farmer.id !== undefined) {
          await db.offlineFarmers.delete(farmer.id);
        }
      },
    );

    return {
      success: true,
      message: "Farmer deleted successfully",
    };
  } catch (error) {
    console.error("Error deleting offline farmer:", error);

    throw error;
  }
};
